import { EventCandidateRunStatus } from 'src/apis/eventCandidateAPI'

/** The message the API sent with a failed request, as the toasts show it. */
export const getApiErrorMessage = (error: any): string =>
	error?.response?.data?.message ||
	error?.response?.data?.error ||
	error?.message ||
	'Something went wrong'

/** Epoch milliseconds from a number, a numeric string or an ISO date string. */
export const toTimestamp = (value: unknown): number | null => {
	if (value === null || value === undefined || value === '') return null
	const numeric = Number(value)
	if (Number.isFinite(numeric)) return numeric
	const parsed = Date.parse(String(value))
	return Number.isFinite(parsed) ? parsed : null
}

export interface RunDropLine {
	label: string
	count: number
}

export interface RunCitySummary {
	name: string
	found: number
	created: number
	droppedTotal: number
	dropped: RunDropLine[]
	error: string
}

// none: no run on record. problems: it ended, but some city failed.
export type RunState =
	| 'none'
	| 'running'
	| 'failed'
	| 'skipped'
	| 'problems'
	| 'finished'

export interface RunSummary {
	state: RunState
	startedAt: number | null
	finishedAt: number | null
	found: number
	created: number
	droppedTotal: number
	dropped: RunDropLine[]
	cities: RunCitySummary[]
	error: string
	// Why the run was skipped, in words; empty when it was not.
	skipped: string
}

const count = (value: unknown) => {
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : 0
}

/** The run code the API sends when the day's event limit is already used up. */
export const DAILY_LIMIT_REACHED = 'daily_limit_reached'
const DEFAULT_DAILY_LIMIT = 20

/** What to tell staff when a run stopped because today's limit was reached; empty for any other run. */
export const dailyLimitNotice = (status?: EventCandidateRunStatus | null) => {
	if (!status || status.skipped !== DAILY_LIMIT_REACHED) return ''
	const limit = Number(status.daily_limit)
	const shown = Number.isFinite(limit) && limit > 0 ? limit : DEFAULT_DAILY_LIMIT
	return `Today's limit of ${shown} events is reached. No search was run, so nothing was charged.`
}

/** `end_time_missing` to `End time missing`, for codes with no wording of their own. */
export const humanizeCode = (code: unknown) => {
	const words = String(code ?? '')
		.replace(/_/g, ' ')
		.trim()
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : ''
}

const toDropLines = (dropped: unknown): RunDropLine[] => {
	if (!dropped || typeof dropped !== 'object' || Array.isArray(dropped)) {
		return []
	}
	return Object.entries(dropped as Record<string, unknown>)
		.map(([reason, value]) => ({
			label: humanizeCode(reason),
			count: count(value),
		}))
		.filter((line) => line.label && line.count > 0)
}

const total = (lines: RunDropLine[]) =>
	lines.reduce((sum, line) => sum + line.count, 0)

const describeSkipped = (skipped: unknown) => {
	if (!skipped || (Array.isArray(skipped) && !skipped.length)) return ''
	if (typeof skipped === 'string') return humanizeCode(skipped)
	if (Array.isArray(skipped)) return skipped.map(humanizeCode).join(', ')
	return 'No reason was given'
}

/** The latest run from GET /event-candidate/run/status, ready to show. */
export const summarizeRun = (
	status?: EventCandidateRunStatus | null,
): RunSummary => {
	const source: EventCandidateRunStatus =
		status && typeof status === 'object' ? status : {}
	const dropped = toDropLines(source.dropped)
	const cities = (Array.isArray(source.cities) ? source.cities : [])
		.filter((item) => item && typeof item === 'object')
		.map((item) => {
			const cityDropped = toDropLines(item.dropped)
			return {
				name:
					[item.city, item.country].filter(Boolean).join(', ') || 'Unknown city',
				found: count(item.found),
				created: count(item.created),
				droppedTotal: total(cityDropped),
				dropped: cityDropped,
				error: item.error ? String(item.error) : '',
			}
		})
	const startedAt = toTimestamp(source.started_at)
	const finishedAt = toTimestamp(source.finished_at)
	const error = source.error ? String(source.error) : ''
	const skipped = describeSkipped(source.skipped)

	let state: RunState = 'finished'
	if (source.running) state = 'running'
	else if (error) state = 'failed'
	else if (skipped) state = 'skipped'
	else if (cities.some((city) => city.error)) state = 'problems'
	else if (startedAt === null && finishedAt === null) state = 'none'

	return {
		state,
		startedAt,
		finishedAt,
		found: count(source.found),
		created: count(source.created),
		droppedTotal: total(dropped),
		dropped,
		cities,
		error,
		skipped,
	}
}
